/**
 * Flaticon UIcons (regular rounded) for the browser panels — the same icon pack
 * ROP-teams uses (Bill 2026-11-04: one icon pack across every PCB app).
 *
 * Each export keeps the lucide-react name it replaces and accepts the props the
 * panels passed to lucide (size, className, style, color, aria-hidden), so a
 * screen switches packs by changing only its import line. They render
 * `<i class="fi fi-rr-{slug}">`; each app imports
 * '@flaticon/flaticon-uicons/css/regular/rounded.css' once in main.tsx (bundled
 * from npm — no CDN). Decorative by default (aria-hidden) unless aria-label is set.
 *
 * Slugs: https://www.flaticon.com/uicons (lookup only; the font ships in the bundle).
 */

import type { CSSProperties } from 'react';

export interface FiIconProps {
  /** Pixel size (font-size). Defaults to 24, lucide's default. */
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  color?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
  'aria-label'?: string;
  /** Accepted for lucide compatibility; a font icon has no stroke width. */
  strokeWidth?: number;
}

export function FiIcon({
  slug,
  size = 24,
  className,
  style,
  color,
  'aria-label': ariaLabel,
}: FiIconProps & { slug: string }): JSX.Element {
  const s: CSSProperties = {
    fontSize: size,
    lineHeight: 1,
    // inline-block so a transform (the .spin animation) applies outside flex rows too.
    display: 'inline-block',
    ...(color !== undefined ? { color } : null),
    ...style,
  };
  const cls = `fi fi-rr-${slug}${className ? ` ${className}` : ''}`;
  return ariaLabel ? (
    <i className={cls} style={s} role="img" aria-label={ariaLabel} />
  ) : (
    <i className={cls} style={s} aria-hidden="true" />
  );
}

function fi(slug: string, name: string): (p: FiIconProps) => JSX.Element {
  const C = (p: FiIconProps): JSX.Element => <FiIcon slug={slug} {...p} />;
  (C as { displayName?: string }).displayName = name;
  return C;
}

/** lucide name → Flaticon slug, for tests and for looking a replacement up. */
export const FI_SLUGS = {
  AlertCircle: 'exclamation',
  AlertOctagon: 'octagon-exclamation',
  AlertTriangle: 'triangle-warning',
  Archive: 'archive',
  ArrowLeft: 'arrow-left',
  ArrowRight: 'arrow-right',
  Bell: 'bell',
  Bold: 'bold',
  CheckCircle: 'check-circle',
  CheckCircle2: 'check-circle',
  ChevronDown: 'angle-small-down',
  ChevronLeft: 'angle-small-left',
  ChevronRight: 'angle-small-right',
  ClipboardType: 'paste',
  Code: 'code-simple',
  Columns2: 'table-columns',
  Database: 'database',
  Download: 'download',
  Edit3: 'edit',
  ExternalLink: 'arrow-up-right-from-square',
  Eye: 'eye',
  EyeOff: 'eye-crossed',
  FileStack: 'copy',
  FileText: 'document',
  GitBranch: 'code-branch',
  History: 'time-past',
  Inbox: 'inbox',
  Info: 'info',
  Italic: 'italic',
  Layers: 'layers',
  Link: 'link',
  List: 'list',
  ListOrdered: 'sort-numeric-down',
  Loader2: 'spinner',
  Mail: 'envelope',
  Minus: 'minus',
  PenLine: 'pen-field',
  Pencil: 'pencil',
  Play: 'play',
  PlayCircle: 'play-circle',
  Plus: 'plus',
  PowerOff: 'power',
  Redo2: 'redo',
  RefreshCw: 'refresh',
  RotateCcw: 'rotate-left',
  Save: 'disk',
  Search: 'search',
  Send: 'paper-plane',
  Settings: 'settings',
  Settings2: 'settings-sliders',
  ShieldAlert: 'shield-exclamation',
  ShieldCheck: 'shield-check',
  Skull: 'skull',
  Table: 'table',
  TestTube2: 'test-tube',
  Trash2: 'trash',
  Underline: 'underline',
  Undo2: 'undo',
  Unlink: 'link-slash',
  Upload: 'upload',
  UserCheck: 'user-check',
  UserPlus: 'user-add',
  UserX: 'user-xmark',
  Variable: 'brackets-curly',
  Workflow: 'workflow',
  X: 'cross-small',
  XCircle: 'cross-circle',
  ZoomIn: 'zoom-in',
  ZoomOut: 'zoom-out',
} as const;

export const AlertCircle = fi(FI_SLUGS.AlertCircle, 'AlertCircle');
export const AlertOctagon = fi(FI_SLUGS.AlertOctagon, 'AlertOctagon');
export const AlertTriangle = fi(FI_SLUGS.AlertTriangle, 'AlertTriangle');
export const Archive = fi(FI_SLUGS.Archive, 'Archive');
export const ArrowLeft = fi(FI_SLUGS.ArrowLeft, 'ArrowLeft');
export const ArrowRight = fi(FI_SLUGS.ArrowRight, 'ArrowRight');
export const Bell = fi(FI_SLUGS.Bell, 'Bell');
export const Bold = fi(FI_SLUGS.Bold, 'Bold');
export const CheckCircle = fi(FI_SLUGS.CheckCircle, 'CheckCircle');
export const CheckCircle2 = fi(FI_SLUGS.CheckCircle2, 'CheckCircle2');
export const ChevronDown = fi(FI_SLUGS.ChevronDown, 'ChevronDown');
export const ChevronLeft = fi(FI_SLUGS.ChevronLeft, 'ChevronLeft');
export const ChevronRight = fi(FI_SLUGS.ChevronRight, 'ChevronRight');
export const ClipboardType = fi(FI_SLUGS.ClipboardType, 'ClipboardType');
export const Code = fi(FI_SLUGS.Code, 'Code');
export const Columns2 = fi(FI_SLUGS.Columns2, 'Columns2');
export const Database = fi(FI_SLUGS.Database, 'Database');
export const Download = fi(FI_SLUGS.Download, 'Download');
export const Edit3 = fi(FI_SLUGS.Edit3, 'Edit3');
export const ExternalLink = fi(FI_SLUGS.ExternalLink, 'ExternalLink');
export const Eye = fi(FI_SLUGS.Eye, 'Eye');
export const EyeOff = fi(FI_SLUGS.EyeOff, 'EyeOff');
export const FileStack = fi(FI_SLUGS.FileStack, 'FileStack');
export const FileText = fi(FI_SLUGS.FileText, 'FileText');
export const GitBranch = fi(FI_SLUGS.GitBranch, 'GitBranch');
export const History = fi(FI_SLUGS.History, 'History');
export const Inbox = fi(FI_SLUGS.Inbox, 'Inbox');
export const Info = fi(FI_SLUGS.Info, 'Info');
export const Italic = fi(FI_SLUGS.Italic, 'Italic');
export const Layers = fi(FI_SLUGS.Layers, 'Layers');
export const Link = fi(FI_SLUGS.Link, 'Link');
export const List = fi(FI_SLUGS.List, 'List');
export const ListOrdered = fi(FI_SLUGS.ListOrdered, 'ListOrdered');
export const Loader2 = fi(FI_SLUGS.Loader2, 'Loader2');
export const Mail = fi(FI_SLUGS.Mail, 'Mail');
export const Minus = fi(FI_SLUGS.Minus, 'Minus');
export const PenLine = fi(FI_SLUGS.PenLine, 'PenLine');
export const Pencil = fi(FI_SLUGS.Pencil, 'Pencil');
export const Play = fi(FI_SLUGS.Play, 'Play');
export const PlayCircle = fi(FI_SLUGS.PlayCircle, 'PlayCircle');
export const Plus = fi(FI_SLUGS.Plus, 'Plus');
export const PowerOff = fi(FI_SLUGS.PowerOff, 'PowerOff');
export const Redo2 = fi(FI_SLUGS.Redo2, 'Redo2');
export const RefreshCw = fi(FI_SLUGS.RefreshCw, 'RefreshCw');
export const RotateCcw = fi(FI_SLUGS.RotateCcw, 'RotateCcw');
export const Save = fi(FI_SLUGS.Save, 'Save');
export const Search = fi(FI_SLUGS.Search, 'Search');
export const Send = fi(FI_SLUGS.Send, 'Send');
export const Settings = fi(FI_SLUGS.Settings, 'Settings');
export const Settings2 = fi(FI_SLUGS.Settings2, 'Settings2');
export const ShieldAlert = fi(FI_SLUGS.ShieldAlert, 'ShieldAlert');
export const ShieldCheck = fi(FI_SLUGS.ShieldCheck, 'ShieldCheck');
export const Skull = fi(FI_SLUGS.Skull, 'Skull');
export const Table = fi(FI_SLUGS.Table, 'Table');
export const TestTube2 = fi(FI_SLUGS.TestTube2, 'TestTube2');
export const Trash2 = fi(FI_SLUGS.Trash2, 'Trash2');
export const Underline = fi(FI_SLUGS.Underline, 'Underline');
export const Undo2 = fi(FI_SLUGS.Undo2, 'Undo2');
export const Unlink = fi(FI_SLUGS.Unlink, 'Unlink');
export const Upload = fi(FI_SLUGS.Upload, 'Upload');
export const UserCheck = fi(FI_SLUGS.UserCheck, 'UserCheck');
export const UserPlus = fi(FI_SLUGS.UserPlus, 'UserPlus');
export const UserX = fi(FI_SLUGS.UserX, 'UserX');
export const Variable = fi(FI_SLUGS.Variable, 'Variable');
export const Workflow = fi(FI_SLUGS.Workflow, 'Workflow');
export const X = fi(FI_SLUGS.X, 'X');
export const XCircle = fi(FI_SLUGS.XCircle, 'XCircle');
export const ZoomIn = fi(FI_SLUGS.ZoomIn, 'ZoomIn');
export const ZoomOut = fi(FI_SLUGS.ZoomOut, 'ZoomOut');
